import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { Toaster } from './components/ui/sonner';
import Layout from './components/Layout';

// Pages
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import Leads from './pages/Leads';
import LeadDetail from './pages/LeadDetail';
import Clients from './pages/Clients';
import ClientDetail from './pages/ClientDetail';
import Contracts from './pages/Contracts';
import ContractDetail from './pages/ContractDetail';
import Users from './pages/Users';
import Agents from './pages/Agents';
import Settings from './pages/Settings';
import Profile from './pages/Profile';
import MieProvvigioni from './pages/MieProvvigioni';
import Chat from './pages/Chat';
import Provvigioni from './pages/Provvigioni';
import Companies from './pages/Companies';
import AuthCallback from './pages/AuthCallback';
import Avvisi from './pages/Avvisi';
import RetiVendita from './pages/RetiVendita';
import Comparatore from './pages/Comparatore';
import Rinnovi from './pages/Rinnovi';
import PublicQuotazione from './pages/PublicQuotazione';
import PublicQuote from './pages/PublicQuote';
import AccediScelta from './pages/AccediScelta';
import { PortalLogin, PortalRegister, PortalForgot, PortalReset, RequirePortalAuth } from './pages/PortalAuth';
import { PortalDashboard, PortalImmobile } from './pages/PortalApp';
import PublicFirma from './pages/PublicFirma';
import PrivacyPolicy from './pages/PrivacyPolicy';
import CallCenter from './pages/CallCenter';
import Commissioni from './pages/Commissioni';
import AlberoRete from './pages/AlberoRete';
import ReportMensile from './pages/ReportMensile';

// Router: sub-agente vede la sua vista maturato-per-mese, altri il listino
const ProvvigioniRouter = () => {
  const { user } = useAuth();
  return user?.ruolo === 'sub_agent' ? <MieProvvigioni /> : <Commissioni />;
};

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { isAuthenticated, loading, user, hasRole } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && !hasRole(allowedRoles)) {
    return <Navigate to="/dashboard" replace />;
  }

  return <Layout>{children}</Layout>;
};

// Public Route Component (redirect if authenticated)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route 
        path="/login" 
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        } 
      />
      <Route 
        path="/register" 
        element={
          <PublicRoute>
            <Register />
          </PublicRoute>
        } 
      />
      <Route path="/auth/callback" element={<AuthCallback />} />

      {/* Public Quotazione - no auth, no layout */}
      <Route path="/quotazione/:code" element={<PublicQuotazione />} />
      <Route path="/quote" element={<PublicQuote />} />
      <Route path="/accedi" element={<AccediScelta />} />
      <Route path="/firma/:token" element={<PublicFirma />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />

      {/* Portale Clienti - auth separata, no layout CRM */}
      <Route path="/clienti/login" element={<PortalLogin />} />
      <Route path="/clienti/registrati" element={<PortalRegister />} />
      <Route path="/clienti/password-dimenticata" element={<PortalForgot />} />
      <Route path="/clienti/reset-password" element={<PortalReset />} />
      <Route path="/clienti" element={<RequirePortalAuth><PortalDashboard /></RequirePortalAuth>} />
      <Route path="/clienti/immobili/:id" element={<RequirePortalAuth><PortalImmobile /></RequirePortalAuth>} />

      {/* Protected Routes */}
      <Route 
        path="/dashboard" 
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        } 
      />

      {/* Leads */}
      <Route 
        path="/leads" 
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'sub_agent', 'back_office', 'interno']}>
            <Leads />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/leads/:id" 
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'sub_agent', 'back_office', 'interno']}>
            <LeadDetail />
          </ProtectedRoute>
        } 
      />

      {/* Clients */}
      <Route 
        path="/clients" 
        element={
          <ProtectedRoute>
            <Clients />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/clients/:id" 
        element={
          <ProtectedRoute>
            <ClientDetail />
          </ProtectedRoute>
        } 
      />

      {/* Contracts */}
      <Route 
        path="/contracts" 
        element={
          <ProtectedRoute>
            <Contracts />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/contracts/:id" 
        element={
          <ProtectedRoute>
            <ContractDetail />
          </ProtectedRoute>
        } 
      />

      {/* Agents */}
      <Route 
        path="/agents" 
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'sub_agent', 'back_office', 'interno']}>
            <Agents />
          </ProtectedRoute>
        } 
      />

      {/* Provvigioni */}
      <Route 
        path="/provvigioni" 
        element={
          <ProtectedRoute allowedRoles={['super_admin']}>
            <Provvigioni />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/mie-provvigioni" 
        element={
          <ProtectedRoute allowedRoles={['sub_agent']}>
            <MieProvvigioni />
          </ProtectedRoute>
        } 
      />

      {/* Users */}
      <Route 
        path="/users" 
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'sub_agent', 'back_office', 'interno']}>
            <Users />
          </ProtectedRoute>
        } 
      />

      {/* Settings */}
      <Route 
        path="/settings" 
        element={
          <ProtectedRoute allowedRoles={['super_admin']}>
            <Settings />
          </ProtectedRoute>
        } 
      />

      {/* Companies */}
      <Route 
        path="/companies" 
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'sub_agent', 'back_office', 'interno']}>
            <Companies />
          </ProtectedRoute>
        } 
      />

      {/* Avvisi */}
      <Route 
        path="/avvisi" 
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'sub_agent', 'back_office', 'interno']}>
            <Avvisi />
          </ProtectedRoute>
        } 
      />

      {/* Reti di Vendita */}
      <Route 
        path="/reti-vendita" 
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'back_office', 'interno']}>
            <RetiVendita />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/chat" 
        element={
          <ProtectedRoute>
            <Chat />
          </ProtectedRoute>
        } 
      />

      {/* Comparatore */}
      <Route 
        path="/comparatore" 
        element={
          <ProtectedRoute>
            <Comparatore />
          </ProtectedRoute>
        } 
      />

      {/* Rinnovi */}
      <Route 
        path="/rinnovi" 
        element={
          <ProtectedRoute>
            <Rinnovi />
          </ProtectedRoute>
        } 
      />

      {/* Call Center */}
      <Route 
        path="/callcenter" 
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'sub_agent', 'operatore_call']}>
            <CallCenter />
          </ProtectedRoute>
        } 
      />

      {/* Profile */}
      <Route 
        path="/profile" 
        element={
          <ProtectedRoute>
            <Profile />
          </ProtectedRoute>
        } 
      />

      {/* Listino Provvigioni Master / Le mie Provvigioni sub-agente */}
      <Route
        path="/commissioni"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'sub_agent']}>
            <ProvvigioniRouter />
          </ProtectedRoute>
        }
      />

      {/* Albero Rete */}
      <Route
        path="/albero-rete"
        element={
          <ProtectedRoute allowedRoles={['super_admin']}>
            <AlberoRete />
          </ProtectedRoute>
        }
      />

      {/* Report Mensile */}
      <Route
        path="/report-mensile"
        element={
          <ProtectedRoute allowedRoles={['super_admin']}>
            <ReportMensile />
          </ProtectedRoute>
        }
      />

      {/* Redirects: home pubblica per non autenticati, dashboard per agenti loggati */}
      <Route path="/" element={<HomeRouter />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function HomeRouter() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <Navigate to="/dashboard" replace />;
  const standalone = window.matchMedia?.('(display-mode: standalone)').matches || window.navigator.standalone;
  if (standalone) return <Navigate to={localStorage.getItem('portal_token') ? '/clienti' : '/accedi'} replace />;
  return <PublicQuote />;
}

function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <AppRoutes />
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
